import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Twitter,
  Linkedin,
  Clock,
  Calendar,
  ArrowUp,
  Copy,
  Check,
} from "lucide-react";
import Footer from "../components/Footer";

const PerfectHack = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const contentRef = useRef(null);

  // NEW: State and Refs for controlling the FIXED sidebar behavior
  const [tocState, setTocState] = useState("normal"); // 'normal' | 'fixed' | 'bottom'
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const mainGridRef = useRef(null);
  const sidebarContainerRef = useRef(null);

  const articleSections = [
    {
      id: "section1",
      title: "The Era of 'Automated' Exploitation",
      content: `
        <p>In early 2026, the cybersecurity landscape shifted from a battle of wits to a battle of algorithms. The catalyst wasn't a single breach, but the emergence of a new class of "frontier" AI models headlined by <strong>Anthropic’s Claude Mythos</strong>.</p>
        <p>To the casual observer, Mythos is a breakthrough in software engineering. To the seasoned security professional, it is a "canary in the coal mine" for a terrifying reality: the era of AI-powered attacks at scale has officially arrived. This isn't just about faster malware; it’s about a model that can reconstruct source code, chain vulnerabilities, and map internal networks in hours tasks that previously took specialist teams weeks.</p>
        <div class="highlight-box">
          <strong>The Hard Truth:</strong> AI like Mythos doesn't create new vulnerabilities; it simply exposes the ones boards have tolerated for years.
        </div>
      `,
    },
    {
      id: "section2",
      title: "Why the Old Math No Longer Adds Up",
      content: `
        <p>If you’ve been managing cyber risk by weighing the "effort" of an attack against the "probability" of a breach, that logic has officially collapsed.</p>
        <ul>
          <li><strong>The Cost of Infiltration has Flatlined:</strong> What once required a $1M specialist team and six weeks of reconnaissance can now be executed by an AI agent in a few hours for the price of an API call.</li>
          <li><strong>The "Legacy" Trap:</strong> Our Operational Technology (OT) in energy, manufacturing, and utilities was built for reliability, not security. Mythos can find zero-day flaws in these 20-year-old systems that have survived millions of human tests.</li>
          <li><strong>The Funding Gap:</strong> While the threat surface is expanding exponentially, most organizations are only increasing security budgets by 10%. To survive this shift, many businesses will need to double their current cybersecurity spending.</li>
        </ul>
        <blockquote class="text-2xl font-serif italic text-slate-700 border-l-4 border-emerald-500 pl-6 my-10">
          "The vulnerabilities have always been there. AI just found the flashlight."
        </blockquote>
      `,
    },
    {
      id: "section3",
      title: "How Organizations Can Defend Themselves",
      content: `
    <p>
      Cybersecurity must be treated as a critical topic for board consideration, and that requires active ownership, sustained investment, and genuine urgency. 
      <strong>Every organization needs to raise its game, and quickly.</strong>
    </p>

    <h4 class="text-xl font-extrabold text-slate-900 mt-8 mb-4">
      1. Establish a Dedicated AI Threat War Room
    </h4>
    <p>
      The scale of the challenge demands a dedicated team focused on AI-driven threats, using the same AI capabilities as adversaries to proactively test and expose weaknesses before they are exploited. 
      These tools must be used defensively to <strong>continuously scan for vulnerabilities, detect anomalous behavior, and accelerate response</strong>. 
      The objective is not just to defend against AI, but to <strong>operationalize it as a core part of security</strong>.
    </p>
    <p>
      Most organizations already have the necessary expertise in-house. Reallocating this talent into a focused, cross-functional war room is far more effective than external hiring, as 
      <strong>internal teams understand the environment in ways outsiders cannot</strong>. 
      This is not a temporary initiative; it is a <strong>permanent shift in how security is executed</strong>.
    </p>

    <h4 class="text-xl font-extrabold text-slate-900 mt-8 mb-4">
      2. Strengthen Foundational Cybersecurity Capabilities
    </h4>
    <p>
      Strong fundamentals are non-negotiable, and most organizations still have significant ground to cover. 
      Years of underinvestment have created vulnerabilities that AI-driven attacks will expose at speed. 
      <strong>Addressing this is not optional it is critical to survival.</strong>
    </p>

    <h4 class="text-xl font-extrabold text-slate-900 mt-8 mb-4">
      3. Address Urgent Risks to Operational Technology (OT) Environments
    </h4>
    <p>
      Organizations with large OT environments face a sharper challenge. Critical sectors like energy, utilities, manufacturing, and transport rely on aging systems that 
      <strong>cannot be easily patched without risking operations</strong>.
    </p>
    <p>
      Built for reliability, not security, these systems are highly exposed to AI-driven attacks that can 
      <strong>uncover vulnerabilities in complex, legacy code</strong>. 
      Where patching isn’t feasible, the focus must shift to strict segmentation, OT-specific anomaly detection, and 
      <strong>minimizing any internet exposure</strong>.
    </p>
  `,
    },
    {
      id: "section4",
      title: "Leadership’s Liability",
      content: `
        <p>For years, organizations didn’t underinvest in cybersecurity by accident they chose to. AI has now exposed that decision at scale. The assumption that attacks require effort and are therefore unlikely has collapsed. Sophisticated attacks can be launched at near-zero cost, making every unpatched system a realistic target.</p>
        <p>Yet most companies still <strong>spend less than one percent of revenue on security, even as breaches surge and losses hit record highs</strong>. Regulators are raising expectations, and cybersecurity is no longer a technical issue it is a leadership responsibility, and delaying it only makes the consequences inevitable.</p>
      `,
    },
    {
      id: "section5",
      title: "Defense in Depth: Survival, Not Perfection",
      content: `
        <p>Strong cybersecurity fundamentals are not minor technical details; they are the architectural and operational decisions that determine whether an organization can withstand AI-enabled attacks. The following tactical priorities can help close the gap:</p>
        
        <h5 class="font-bold text-slate-900 mt-8 mb-2">Automated Patching</h5>
        <p>The window between vulnerability discovery and exploitation has collapsed to near zero. Manual patching cycles simply cannot keep up. Organizations must move to automated, risk-prioritized patching that continuously identifies and remediates exposures before they are weaponized. <strong>In an AI-driven threat landscape, unpatched systems are an inevitability waiting to be exploited.</strong></p>

        <h5 class="font-bold text-slate-900 mt-8 mb-2">Zero Trust Architecture</h5>
        <p>The idea of a trusted internal network is obsolete. Zero trust replaces this with continuous verification of every user, device, and request regardless of location. <strong>Access is granted based on context and continuously reassessed</strong>. In a world of AI-enabled lateral movement, zero trust is what <strong>limits how far an attacker can go</strong>.</p>

        <h5 class="font-bold text-slate-900 mt-8 mb-2">Anomaly Detection</h5>
        <p>AI-driven attacks rarely match known signatures which makes traditional detection increasingly ineffective. The new baseline is behavioral analysis identifying deviations in how users, systems, and data normally interact. Anomaly detection shifts defense from recognizing known threats to spotting the unknown. It is one of the few ways to <strong>detect attacks that have never been seen before</strong>.</p>

        <h5 class="font-bold text-slate-900 mt-8 mb-2">Modernizing Identity Controls</h5>
        <p>Identity has become the primary attack vector. AI has made large-scale credential theft and phishing highly effective and scalable. Strong identity controls such as phishing-resistant MFA ensure that stolen credentials alone are not enough to gain access. This <strong>reduces the blast radius of a breach and prevents attackers from escalating privileges</strong> even after initial compromise.</p>

        <h5 class="font-bold text-slate-900 mt-8 mb-2">Reducing Legacy Technical Debt</h5>
        <p>Legacy systems are often the weakest link because they cannot support modern security controls. They persist in environments because they are difficult to replace but attackers target them for the same reason. AI accelerates the discovery of weaknesses in these systems making them high-risk assets. Addressing technical debt is a necessary step toward <strong>reducing systemic exposure</strong>.</p>

        <h5 class="font-bold text-slate-900 mt-8 mb-2">Addressing Supply Chain Risk</h5>
        <p>Organizations are no longer attacked in isolation. Attackers increasingly target vendors, suppliers, and third-party software as entry points into larger environments. A strong internal security posture can be undermined by a weak partner. This makes third-party risk management a critical component of cybersecurity requiring <strong>continuous visibility into how well partners can defend against modern AI-enabled threats</strong>.</p>
      `,
    },
    {
      id: "section6",
      title: "The Synthera Perspective",
      content: `
    <div class="mt-12 pt-10 border-t-2 border-emerald-500/30">
      <h3 class="text-2xl font-extrabold text-slate-900 mb-6 tracking-tight">
        From Defense to Resilience
      </h3>

      <p class="text-lg text-slate-700 leading-relaxed mb-6">

      At Synthera, we operate on a simple truth: it’s not if an attacker gets in, but how far they can go. AI has removed friction, accelerated attacks, and exposed architectural weaknesses. Security today cannot rely on prevention or detection alone, it must be designed to contain impact.      </p>

      <p class="text-lg text-slate-700 leading-relaxed mb-8">
      Environmental hardening makes this real. Segmentation, least privilege, and zero trust limit lateral movement. Anomaly detection, continuous monitoring, and rapid patching enable response at machine speed. Equally important is reducing exposure by placing applications/Users/Agents behind secure proxies, ensuring critical assets are never directly exposed and the attack surface is minimized.
      </p>
      <p class="text-lg text-slate-700 leading-relaxed mb-8">

      This is not a tooling problem, it is a mindset shift. From reactive controls to resilient architecture, from isolated solutions to integrated defense, and from technical ownership to leadership accountability. AI is not creating new vulnerabilities, it is exposing existing ones.

      </p>
      <p class="mt-6 text-emerald-600 font-bold text-lg italic">
        Final Thought:
        <span class="text-slate-800 font-medium not-italic">
        Cybersecurity fundamentals are no longer optional. They are the foundation of resilience.
        </span>
      </p>
    </div>
  `,
    },
  ];

  const tableOfContents = [
    { id: "section1", title: "1. Automated Exploitation" },
    { id: "section2", title: "2. The Failure of Old Math" },
    { id: "section3", title: "3. Defending the Organization" },
    { id: "section4", title: "4. Leadership Liability" },
    { id: "section5", title: "5. Tactical Priorities" },
    { id: "section6", title: "6. The Synthera Perspective" },
  ];

  // FIX: Custom Scroll Logic to force FIXED positioning once past the top section
  useEffect(() => {
    const handleScrollFixed = () => {
      if (!sidebarContainerRef.current || !mainGridRef.current) return;

      const containerRect = sidebarContainerRef.current.getBoundingClientRect();
      const gridRect = mainGridRef.current.getBoundingClientRect();

      const asideElement = sidebarContainerRef.current.querySelector("aside");
      const sidebarHeight = asideElement ? asideElement.offsetHeight : 500;

      const offsetTop = 128; // Represents top-32 placement

      // Force the width to perfectly match the grid column even when 'fixed' pulls it out of flow
      setSidebarWidth(sidebarContainerRef.current.offsetWidth);

      // Check if the article is finished (stops sidebar from hitting footer)
      // 96px accounts for the pb-24 padding on the grid
      if (gridRect.bottom <= offsetTop + sidebarHeight + 96) {
        setTocState("bottom");
      }
      // Check if we have scrolled past the top section -> Make it FIXED
      else if (containerRect.top <= offsetTop) {
        setTocState("fixed");
      }
      // Otherwise, it sits normally at the top
      else {
        setTocState("normal");
      }
    };

    window.addEventListener("scroll", handleScrollFixed);
    window.addEventListener("resize", handleScrollFixed);
    handleScrollFixed(); // Run once on load

    return () => {
      window.removeEventListener("scroll", handleScrollFixed);
      window.removeEventListener("resize", handleScrollFixed);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { root: null, rootMargin: "-20% 0px -60% 0px", threshold: 0 },
    );

    if (contentRef.current) {
      const sections = contentRef.current.querySelectorAll("section[id]");
      sections.forEach((section) => observer.observe(section));
    }

    return () => observer.disconnect();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>The Mythos of the Perfect Hack | Synthera Security</title>

        <meta
          name="description"
          content="Discover how AI-powered attacks are reshaping cybersecurity, why traditional risk models are failing, and how organizations can build resilience against next-generation threats."
        />

        <meta
          name="keywords"
          content="AI Security, Cybersecurity Strategy, AI Cyber Attacks, Zero Trust, Cyber Resilience, Threat Detection, Security Leadership, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="The Mythos of the Perfect Hack | Synthera Security"
        />

        <meta
          property="og:description"
          content="How AI-powered attacks are changing cybersecurity and why organizations must rethink their security strategy."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/blog/perfect-hack-boardroom-strategy"
        />

        <meta property="og:type" content="article" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/blog/perfect-hack-boardroom-strategy"
        />
      </Helmet>

      <div className="min-h-screen bg-white font-sans text-slate-800 relative">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className={`fixed bottom-8 right-8 z-50 p-4 bg-slate-900 text-white rounded-full shadow-2xl transition-all duration-500 hover:bg-[#00a684] ${
            showBackToTop
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10 pointer-events-none"
          }`}
        >
          <ArrowUp className="w-6 h-6" />
        </button>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 lg:pt-40">
          <div className="absolute top-0 left-0 right-0 h-20 bg-[#032d28] z-0  rounded-b-[1.5rem]"></div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-7">
              <nav className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-8">
                <Link to="/" className="text-[#00a684] no-underline">
                  Home
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <Link to="/resources" className="text-[#00a684] no-underline">
                  Resources
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <span className="text-[#00a684]">Blog</span>
              </nav>
              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                  <Calendar size={14} /> May 1, 2026
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <Clock size={14} /> 12m read
                </div>
              </div>

              <h1 className="text-4xl lg:text-[44px] font-extrabold text-slate-900 leading-tight mb-8 tracking-tight">
                The Mythos of the "Perfect Hack": Why Your Boardroom Strategy
                Just Became Obsolete
              </h1>

              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center font-bold text-white shadow-lg text-sm">
                  HM
                </div>

                <div className="leading-tight">
                  <p className="text-base font-bold text-slate-800 mb-0">
                    Harish Malla
                  </p>

                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.12em] mt-0">
                    Cyber Security Consultant
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-red-500 to-emerald-500 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-500"></div>
                <div className="relative overflow-hidden rounded-[2rem] shadow-2xl bg-slate-900 aspect-[4/3]">
                  <img
                    src="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto,w_1200/v1777649809/perfect_hack_zvrhrv.png"
                    alt="AI Cybersecurity Strategy"
                    className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Content Layout - UPDATED with Refs and precise positioning classes */}
          <div
            ref={mainGridRef}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24 items-start relative"
          >
            {/* FIXED LEFT SIDEBAR HANDLED BY JS */}
            <div
              ref={sidebarContainerRef}
              className="lg:col-span-3 hidden lg:block relative h-full"
            >
              <aside
                style={{
                  width: tocState !== "normal" ? `${sidebarWidth}px` : "auto",
                }}
                className={`
                ${tocState === "fixed" ? "fixed top-32" : ""}
                ${tocState === "bottom" ? "absolute bottom-24" : ""}
                ${tocState === "normal" ? "relative" : ""}
                space-y-8 max-h-[calc(100vh-10rem)] overflow-y-auto no-scrollbar
              `}
              >
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-8">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.3em] mb-6 text-slate-400 border-b pb-4">
                    Table of Contents
                  </h4>
                  <ul className="space-y-4">
                    {tableOfContents.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className={`text-sm font-bold leading-snug block no-underline transition-all ${
                            activeSection === item.id
                              ? "text-emerald-600 translate-x-1"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <p className="text-[11px] font-black uppercase tracking-[0.3em] mb-4 text-slate-400">
                    Share Insight
                  </p>
                  <div className="flex gap-3">
                    <button className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all">
                      <Twitter size={18} className="mx-auto" />
                    </button>
                    <a
                      href="https://www.linkedin.com/feed/update/urn:li:activity:7456937237977522176"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                    >
                      <Linkedin size={18} className="mx-auto" />
                    </a>
                    <button
                      onClick={handleCopyLink}
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                    >
                      {copied ? (
                        <Check size={18} className="mx-auto" />
                      ) : (
                        <Copy size={18} className="mx-auto" />
                      )}
                    </button>
                  </div>
                </div>
              </aside>
            </div>

            {/* MAIN ARTICLE AREA */}
            <div className="lg:col-span-9 max-w-3xl" ref={contentRef}>
              <div className="prose prose-lg max-w-none text-slate-600 leading-relaxed blog-content">
                {articleSections.map((section) => (
                  <section
                    key={section.id}
                    id={section.id}
                    className="mb-16 pt-8 border-t border-slate-100 first:border-0 first:pt-0"
                  >
                    <h2 className="text-3xl font-bold text-slate-800 m-0 mb-8 leading-tight">
                      {section.title}
                    </h2>
                    <div
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Footer />

        <style>{`
        .blog-content p { margin-bottom: 1.75rem; font-size: 1.125rem; line-height: 1.85; }
        .blog-content ul { margin-bottom: 2rem; list-style-type: none; padding-left: 0; }
        .blog-content li { position: relative; padding-left: 1.75rem; margin-bottom: 0.75rem; font-weight: 500; }
        .blog-content li::before { content: "→"; color: #10b981; position: absolute; left: 0; font-weight: bold; }
        .highlight-box { background: #fff1f2; border-left: 4px solid #f43f5e; padding: 2rem; border-radius: 0 1.5rem 1.5rem 0; margin: 2.5rem 0; color: #9f1239; }
        html { scroll-behavior: smooth; }

        /* Hides the scrollbar for the Table of Contents while keeping it scrollable */
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      </div>
    </>
  );
};

export default PerfectHack;
